/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { ShieldCheck, Search, Activity } from "lucide-react";
import { db } from "../../firebase";

function formatDate(value) {
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      timeZone: "Europe/London",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function niceAction(value) {
  return String(value || "unknown")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AuditDashboard({ schoolId }) {
  const [logs, setLogs] = useState([]);
  const [evidenceLogs, setEvidenceLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  async function loadLogs() {
    if (!schoolId) return;

    setLoading(true);

    try {
      const [auditSnap, evidenceSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "schools", schoolId, "auditLogs"),
            orderBy("createdAt", "desc"),
            limit(100)
          )
        ),
        getDocs(
          query(
            collection(db, "schools", schoolId, "absenceEvidenceAuditLogs"),
            orderBy("viewedAt", "desc"),
            limit(100)
          )
        ),
      ]);

      setLogs(auditSnap.docs.map((d) => ({ id: d.id, source: "system", ...d.data() })));
      setEvidenceLogs(
        evidenceSnap.docs.map((d) => ({
          id: d.id,
          source: "evidence",
          action: d.data()?.action || "evidence_viewed",
          userId: d.data()?.viewedByUid,
          createdAt: d.data()?.viewedAt,
          targetId: d.data()?.absenceId,
          meta: { proofPath: d.data()?.proofPath },
          ...d.data(),
        }))
      );
    } catch (err) {
      console.error("Failed to load audit logs:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLogs();
  }, [schoolId]);

  const mergedLogs = useMemo(() => {
    const all = [...logs, ...evidenceLogs];

    const q = search.trim().toLowerCase();

    return all
      .filter((log) => {
        if (!q) return true;

        return [
          log.action,
          log.userId,
          log.viewedByUid,
          log.targetId,
          log.absenceId,
          log.schoolId,
          JSON.stringify(log.meta || {}),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || a.viewedAt?.toDate?.() || new Date(0);
        const bDate = b.createdAt?.toDate?.() || b.viewedAt?.toDate?.() || new Date(0);
        return bDate.getTime() - aDate.getTime();
      });
  }, [logs, evidenceLogs, search]);

  return (
    <section style={styles.page}>
      <div style={styles.header}>
        <div>
          <p style={styles.eyebrow}>Security Centre</p>
          <h2 style={styles.title}>Audit Dashboard</h2>
          <p style={styles.subtitle}>
            Track sensitive admin actions, evidence access, invites, broadcasts, and school operations.
          </p>
        </div>

        <div style={styles.badge}>
          <ShieldCheck size={18} />
          Compliance logs
        </div>
      </div>

      <div style={styles.toolbar}>
        <Search size={18} />
        <input
          style={styles.search}
          placeholder="Search action, user ID, target ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.empty}>Loading audit logs...</div>
        ) : mergedLogs.length ? (
          mergedLogs.map((log) => (
            <div key={`${log.source}-${log.id}`} style={styles.row}>
              <div style={styles.icon}>
                <Activity size={18} />
              </div>

              <div style={styles.main}>
                <strong>{niceAction(log.action)}</strong>
                <span>
                  {log.source === "evidence" ? "Evidence access" : "System event"}
                </span>
              </div>

              <div style={styles.meta}>
                <span>{formatDate(log.createdAt || log.viewedAt)}</span>
                <small>User: {log.userId || log.viewedByUid || "—"}</small>
                <small>Target: {log.targetId || log.absenceId || "—"}</small>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.empty}>No audit logs found yet.</div>
        )}
      </div>
    </section>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: 18,
  },
  header: {
    padding: 24,
    borderRadius: 28,
    background: "var(--pp-brand-dark-gradient)",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "center",
  },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    fontWeight: 900,
    opacity: 0.8,
  },
  title: {
    margin: "8px 0 0",
    fontSize: 34,
    fontWeight: 950,
  },
  subtitle: {
    margin: "8px 0 0",
    color: "rgba(255,255,255,0.82)",
    maxWidth: 720,
    lineHeight: 1.6,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.18)",
    padding: "10px 14px",
    borderRadius: 999,
    fontWeight: 900,
  },
  toolbar: {
    background: "#fff",
    border: "1px solid #e5ebf7",
    borderRadius: 18,
    minHeight: 52,
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  search: {
    border: 0,
    outline: 0,
    flex: 1,
    fontSize: 15,
  },
  card: {
    background: "#fff",
    border: "1px solid #e5ebf7",
    borderRadius: 24,
    overflow: "hidden",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "44px 1fr auto",
    gap: 14,
    alignItems: "center",
    padding: 16,
    borderBottom: "1px solid #eef2f7",
  },
  icon: {
    width: 44,
    height: 44,
    borderRadius: 16,
    background: "#eff6ff",
    color: "#2563eb",
    display: "grid",
    placeItems: "center",
  },
  main: {
    display: "grid",
    gap: 4,
  },
  meta: {
    display: "grid",
    gap: 4,
    textAlign: "right",
    color: "#64748b",
    fontSize: 13,
  },
  empty: {
    padding: 24,
    color: "#64748b",
    textAlign: "center",
  },
};